/**
 * Selection-scoped Super Admin → Fleet inventory polling.
 *
 * Scale rule: never poll unselected devices. Fleet ingest stays event-driven
 * on the device; SA only GETs Fleet for the actively viewed Zone.
 *
 * Idle Remote Console: one-shot package GET on mount (no timer).
 * Timer polling is only for in-flight software OTA Installed convergence.
 */

import {
  SOFTWARE_INVENTORY_OTA_POLL_MS,
  SOFTWARE_INVENTORY_OTA_SYNC_TIMEOUT_MS,
} from "./softwareInventoryOtaSync";

/**
 * Idle selected-device Fleet refresh interval.
 * 0 = initial GET only; do not timer-poll while the console sits idle.
 */
export const SOFTWARE_INVENTORY_SELECTED_POLL_MS = 0;

/**
 * Offline idle GETs are not timer-polled either. OTA in-flight still uses
 * {@link SOFTWARE_INVENTORY_OTA_POLL_MS} while the console is visible.
 */
export const SOFTWARE_INVENTORY_OFFLINE_POLL_MS = 0;

/**
 * Package-status reads must not refetch because the browser tab focused,
 * reconnected, or the Network Inspector was clicked.
 */
export const SOFTWARE_INVENTORY_PACKAGE_QUERY_OPTIONS = {
  refetchOnFocus: false,
  refetchOnReconnect: false,
  skipPollingIfUnfocused: true,
} as const;

export {
  SOFTWARE_INVENTORY_OTA_POLL_MS,
  SOFTWARE_INVENTORY_OTA_SYNC_TIMEOUT_MS,
};

export type SoftwareInventoryPollingArgs = {
  /** Exactly one Zone selected (Remote Console inventory scope). */
  singleZoneSelected: boolean;
  /** Remote Console panel mounted / modal open. */
  consoleOpen: boolean;
  /** document.visibilityState === 'hidden' */
  documentHidden: boolean;
  /** Bounded post-OTA sync active for this console session. */
  otaSyncActive: boolean;
  /** Fleet device appears Offline — stretch routine polls. */
  deviceAppearsOffline?: boolean;
};

/**
 * Fleet package GET polling interval for the actively viewed device.
 * 0 = no timer (idle, unselected, closed, hidden tab, or bulk selection).
 *
 * Never issues refresh_software_inventory — Fleet GET only.
 */
export function resolveSoftwareInventoryFleetPollingIntervalMs(
  args: SoftwareInventoryPollingArgs
): number {
  if (!args.consoleOpen) return 0;
  if (!args.singleZoneSelected) return 0;
  if (args.documentHidden) return 0;

  if (args.otaSyncActive) {
    return SOFTWARE_INVENTORY_OTA_POLL_MS;
  }

  return SOFTWARE_INVENTORY_SELECTED_POLL_MS;
}

/** Package GET subscription allowed only for single-Zone console scope. */
export function shouldSubscribeSoftwareInventoryQueries(args: {
  singleZoneSelected: boolean;
  consoleOpen: boolean;
}): boolean {
  return args.consoleOpen && args.singleZoneSelected;
}

/**
 * Which package lanes to subscribe for the open console.
 * Idle: only the visible tab. OTA sync: include the OTA target lane.
 */
export function shouldFetchSoftwareInventoryLane(args: {
  singleZoneSelected: boolean;
  consoleOpen: boolean;
  laneActive: boolean;
  otaSyncActive: boolean;
  laneIsOtaTarget: boolean;
}): boolean {
  if (!shouldSubscribeSoftwareInventoryQueries(args)) return false;
  if (args.laneActive) return true;
  if (args.otaSyncActive && args.laneIsOtaTarget) return true;
  return false;
}

/**
 * Generation guard so a slow Fleet response for device A cannot paint device B.
 */
export function createInventoryRequestGeneration(): {
  bump: () => number;
  isCurrent: (generation: number) => boolean;
  current: () => number;
} {
  let generation = 0;
  return {
    bump: () => {
      generation += 1;
      return generation;
    },
    isCurrent: (g: number) => g === generation,
    current: () => generation,
  };
}
